import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { rupee, apiError } from "@/lib/api";
import { StatCard } from "@/components/DashboardShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Spinner, EmptyState } from "@/components/States";
import { Users, Wallet, Percent, BadgeIndianRupee, ShieldCheck, ShieldAlert, Gift } from "lucide-react";
import { toast } from "sonner";

const Table = ({ children }) => <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white"><table className="w-full text-sm">{children}</table></div>;
const Th = ({ children }) => <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;

export default function AdminAffiliates() {
  return (
    <div className="space-y-4" data-testid="admin-affiliates-panel">
      <Tabs defaultValue="affiliates">
        <TabsList className="flex flex-wrap gap-1 rounded-2xl border border-slate-200 bg-white p-2 [&>button[data-state=active]]:bg-brand-soft [&>button[data-state=active]]:text-brand">
          <TabsTrigger value="affiliates" data-testid="adm-aff-tab-affiliates">Influencers</TabsTrigger>
          <TabsTrigger value="brands" data-testid="adm-aff-tab-brands">Brands</TabsTrigger>
          <TabsTrigger value="withdrawals" data-testid="adm-aff-tab-withdrawals">Withdrawals</TabsTrigger>
          <TabsTrigger value="config" data-testid="adm-aff-tab-config">Commission rates</TabsTrigger>
        </TabsList>
        <TabsContent value="affiliates" className="mt-4"><Affiliates /></TabsContent>
        <TabsContent value="brands" className="mt-4"><Brands /></TabsContent>
        <TabsContent value="withdrawals" className="mt-4"><Withdrawals /></TabsContent>
        <TabsContent value="config" className="mt-4"><Config /></TabsContent>
      </Tabs>
    </div>
  );
}

const S_BADGE = { approved: "bg-brand-soft text-brand", pending: "bg-amber-50 text-amber-warm", rejected: "bg-red-50 text-red-500", suspended: "bg-red-50 text-red-500" };

function Affiliates() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["adm-affiliates"], queryFn: () => api.get("/admin/affiliates").then((r) => r.data), refetchInterval: 10000 });
  const review = async (id, action) => {
    try { await api.patch(`/admin/affiliates/${id}`, { action }); toast.success(`Influencer ${action}d`); qc.invalidateQueries({ queryKey: ["adm-affiliates"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!data) return <Spinner />;
  const totalPaid = data.reduce((s, a) => s + (a.wallet_lifetime || 0), 0);
  const pending = data.filter((a) => a.status === "pending").length;
  if (!data.length) return <EmptyState icon={Users} title="No influencers yet" subtitle="Influencers appear here after they sign up via the Influencer role." />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard label="Total influencers" value={data.length} icon={Users} color="#EC4899" />
        <StatCard testid="adm-aff-pending" label="Pending approval" value={pending} icon={Users} color="#F59E0B" />
        <StatCard label="Lifetime commissions" value={rupee(totalPaid)} icon={Wallet} color="#059669" />
      </div>
      <Table>
        <thead><tr className="border-b border-slate-100"><Th>Name</Th><Th>Code</Th><Th>Balance</Th><Th>Pending</Th><Th>Orders</Th><Th>Verified</Th><Th>Status</Th><Th>Actions</Th></tr></thead>
        <tbody>
          {data.map((a) => (
            <tr key={a.id} className="border-b border-slate-50">
              <Td><p className="font-semibold text-slate-800">{a.name}</p><p className="text-xs text-slate-400">{a.email}</p></Td>
              <Td className="font-mono text-xs text-slate-600">{a.affiliate_code}</Td>
              <Td className="font-mono text-slate-800">{rupee(a.wallet_balance)}</Td>
              <Td className="font-mono text-slate-500">{rupee(a.wallet_pending)}</Td>
              <Td className="text-slate-500">{a.referred_orders}</Td>
              <Td>{a.verified
                ? <span data-testid={`adm-aff-verified-${a.id}`} className="inline-flex items-center gap-1 rounded-md bg-brand-soft px-2 py-0.5 text-[11px] font-bold text-brand"><ShieldCheck className="h-3 w-3" /> Verified</span>
                : <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-400"><ShieldAlert className="h-3 w-3" /> Unverified</span>}</Td>
              <Td><span className={`rounded-md px-2 py-0.5 text-[11px] font-bold capitalize ${S_BADGE[a.status] || "bg-slate-100 text-slate-600"}`}>{a.status}</span></Td>
              <Td>
                <div className="flex gap-1.5">
                  {a.status !== "approved" && <button onClick={() => review(a.id, "approve")} data-testid={`adm-aff-approve-${a.id}`} className="rounded-lg bg-brand px-2.5 py-1 text-xs font-bold text-white">Approve</button>}
                  {a.status === "approved" && <button onClick={() => review(a.id, "suspend")} data-testid={`adm-aff-suspend-${a.id}`} className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600">Suspend</button>}
                  {a.status === "pending" && <button onClick={() => review(a.id, "reject")} data-testid={`adm-aff-reject-${a.id}`} className="rounded-lg border border-red-200 px-2.5 py-1 text-xs font-semibold text-red-500">Reject</button>}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

const W_BADGE = { requested: "bg-amber-50 text-amber-warm", paid: "bg-brand-soft text-brand", rejected: "bg-red-50 text-red-500" };

function Withdrawals() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["adm-withdrawals"], queryFn: () => api.get("/admin/affiliate/withdrawals").then((r) => r.data), refetchInterval: 10000 });
  const review = async (id, action) => {
    const note = action === "reject" ? (window.prompt("Reason for rejection (optional):") || "") : "";
    try { await api.patch(`/admin/affiliate/withdrawals/${id}`, { action, note }); toast.success(action === "approve" ? "Marked as paid" : "Rejected & refunded"); qc.invalidateQueries({ queryKey: ["adm-withdrawals"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!data) return <Spinner />;
  const pendingAmt = data.filter((w) => w.status === "requested").reduce((s, w) => s + w.amount, 0);
  if (!data.length) return <EmptyState icon={BadgeIndianRupee} title="No withdrawal requests" subtitle="Approved influencers can request payouts from their wallet." />;
  return (
    <div className="space-y-4">
      <StatCard label="Pending payout amount" value={rupee(pendingAmt)} icon={BadgeIndianRupee} color="#F59E0B" />
      <Table>
        <thead><tr className="border-b border-slate-100"><Th>Influencer</Th><Th>Amount</Th><Th>Payout to</Th><Th>Status</Th><Th>Requested</Th><Th>Actions</Th></tr></thead>
        <tbody>
          {data.map((w) => (
            <tr key={w.id} className="border-b border-slate-50">
              <Td className="font-semibold text-slate-800">{w.influencer_name}</Td>
              <Td className="font-mono text-slate-800">{rupee(w.amount)}</Td>
              <Td className="text-xs text-slate-500">{w.payout?.method === "bank" ? `${w.payout?.account_number || "-"} (${w.payout?.ifsc || "-"})` : (w.payout?.upi_id || "-")}</Td>
              <Td><span className={`rounded-md px-2 py-0.5 text-[11px] font-bold capitalize ${W_BADGE[w.status] || "bg-slate-100 text-slate-600"}`}>{w.status}</span></Td>
              <Td className="text-slate-500">{(w.created_at || "").slice(0, 10)}</Td>
              <Td>
                {w.status === "requested" ? (
                  <div className="flex gap-1.5">
                    <button onClick={() => review(w.id, "approve")} data-testid={`adm-wd-approve-${w.id}`} className="rounded-lg bg-brand px-2.5 py-1 text-xs font-bold text-white">Mark paid</button>
                    <button onClick={() => review(w.id, "reject")} data-testid={`adm-wd-reject-${w.id}`} className="rounded-lg border border-red-200 px-2.5 py-1 text-xs font-semibold text-red-500">Reject</button>
                  </div>
                ) : <span className="text-xs text-slate-400">{w.note || "-"}</span>}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function Config() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["adm-aff-config"], queryFn: () => api.get("/admin/affiliate/config").then((r) => r.data) });
  const { data: cats } = useQuery({ queryKey: ["cats"], queryFn: () => api.get("/categories").then((r) => r.data) });
  const [def, setDef] = useState(0);
  const [minW, setMinW] = useState(500);
  const [rates, setRates] = useState({});
  const [coupon, setCoupon] = useState("");
  const [wtext, setWtext] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (data) { setDef(data.default_percent); setMinW(data.min_withdrawal); setRates(data.rates || {}); setCoupon(data.welcome_coupon || ""); setWtext(data.welcome_text || ""); } }, [data]);

  const save = async () => {
    setBusy(true);
    const clean = {};
    Object.entries(rates).forEach(([k, v]) => { if (v !== "" && v != null) clean[k] = parseFloat(v); });
    try { await api.put("/admin/affiliate/config", { default_percent: parseFloat(def) || 0, min_withdrawal: parseFloat(minW) || 0, rates: clean, welcome_coupon: coupon, welcome_text: wtext }); toast.success("Commission settings saved"); qc.invalidateQueries({ queryKey: ["adm-aff-config"] }); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  if (!data) return <Spinner />;
  return (
    <div className="max-w-2xl space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
      <h3 className="flex items-center gap-2 font-head font-bold text-slate-900"><Percent className="h-5 w-5 text-brand" /> Commission settings</h3>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-sm"><span className="mb-1 block font-semibold text-slate-600">Default commission %</span>
          <input type="number" value={def} onChange={(e) => setDef(e.target.value)} data-testid="adm-aff-default-pct" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand" /></label>
        <label className="text-sm"><span className="mb-1 block font-semibold text-slate-600">Minimum withdrawal ₹</span>
          <input type="number" value={minW} onChange={(e) => setMinW(e.target.value)} data-testid="adm-aff-min-withdrawal" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand" /></label>
      </div>
      <div>
        <p className="mb-2 text-sm font-semibold text-slate-600">Per-category commission % <span className="font-normal text-slate-400">(leave blank to use default)</span></p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {(cats || []).map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
              <span className="truncate text-sm text-slate-700">{c.name}</span>
              <input type="number" value={rates[c.slug] ?? ""} placeholder={String(def)} onChange={(e) => setRates({ ...rates, [c.slug]: e.target.value })}
                data-testid={`adm-aff-rate-${c.slug}`} className="w-20 rounded-lg border border-slate-200 px-2 py-1.5 text-right text-sm outline-none focus:border-brand" />
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-pink-100 bg-pink-50/50 p-4">
        <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><Gift className="h-4 w-4 text-pink-600" /> Referral welcome offer <span className="font-normal text-slate-400">(shown to shoppers who arrive via a referral link)</span></p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Welcome coupon code (e.g. WELCOME100)" data-testid="adm-aff-welcome-coupon" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand" />
          <input value={wtext} onChange={(e) => setWtext(e.target.value)} placeholder="Banner text (e.g. Get ₹100 off your first order!)" data-testid="adm-aff-welcome-text" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand" />
        </div>
        <p className="mt-1.5 text-[11px] text-slate-400">Create the coupon under the Coupons tab, then enter its code here to promote it on referral visits.</p>
      </div>
      <button onClick={save} disabled={busy} data-testid="adm-aff-config-save" className="rounded-xl bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-deep disabled:opacity-60">Save settings</button>
    </div>
  );
}

function Brands() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["adm-aff-brands"], queryFn: () => api.get("/admin/affiliate/brands").then((r) => r.data) });
  const { data: allBrands } = useQuery({ queryKey: ["adm-brands"], queryFn: () => api.get("/admin/brands").then((r) => r.data) });
  const [brand, setBrand] = useState("");
  const [pct, setPct] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!brand.trim()) return toast.error("Select or enter a brand");
    setBusy(true);
    try { await api.post("/admin/affiliate/brands", { brand: brand.trim(), commission_percent: parseFloat(pct) || 0 }); toast.success("Brand added to affiliate program"); setBrand(""); setPct(""); qc.invalidateQueries({ queryKey: ["adm-aff-brands"] }); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const update = async (id, payload) => {
    try { await api.patch(`/admin/affiliate/brands/${id}`, payload); qc.invalidateQueries({ queryKey: ["adm-aff-brands"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/affiliate/brands/${id}`); toast.success("Removed"); qc.invalidateQueries({ queryKey: ["adm-aff-brands"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!data) return <Spinner />;
  return (
    <div className="space-y-4" data-testid="adm-aff-brands-panel">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-1 flex items-center gap-2 font-head font-bold text-slate-900"><Gift className="h-5 w-5 text-pink-600" /> Add a brand to the affiliate program</h3>
        <p className="mb-3 text-xs text-slate-500">Influencers earn this commission % on referred orders of the brand's products. Brand commission overrides category rates.</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input list="brand-options" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Brand name" data-testid="adm-aff-brand-name" className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand" />
          <datalist id="brand-options">
            {(allBrands || []).map((b) => <option key={b.id} value={b.name} />)}
          </datalist>
          <input type="number" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="Commission %" data-testid="adm-aff-brand-pct" className="w-40 rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand" />
          <button onClick={add} disabled={busy} data-testid="adm-aff-brand-add" className="rounded-xl bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-deep disabled:opacity-60">Add</button>
        </div>
      </div>
      {data.length === 0 ? (
        <EmptyState icon={Gift} title="No affiliate brands yet" subtitle="Add brands above to enroll them in the affiliate program." />
      ) : (
        <Table>
          <thead><tr className="border-b border-slate-100"><Th>Brand</Th><Th>Commission %</Th><Th>Active</Th><Th>Actions</Th></tr></thead>
          <tbody>
            {data.map((b) => (
              <tr key={b.id} className="border-b border-slate-50">
                <Td className="font-semibold text-slate-800">{b.brand}</Td>
                <Td>
                  <input type="number" defaultValue={b.commission_percent} data-testid={`adm-aff-brand-edit-${b.id}`}
                    onBlur={(e) => { const v = parseFloat(e.target.value); if (v !== b.commission_percent) update(b.id, { commission_percent: v }); }}
                    className="w-24 rounded-lg border border-slate-200 px-2 py-1 text-right text-sm outline-none focus:border-brand" />
                </Td>
                <Td>
                  <button onClick={() => update(b.id, { active: !b.active })} data-testid={`adm-aff-brand-toggle-${b.id}`}
                    className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${b.active ? "bg-brand-soft text-brand" : "bg-slate-100 text-slate-500"}`}>{b.active ? "Active" : "Inactive"}</button>
                </Td>
                <Td><button onClick={() => remove(b.id)} data-testid={`adm-aff-brand-del-${b.id}`} className="rounded-lg border border-red-200 px-2.5 py-1 text-xs font-semibold text-red-500">Remove</button></Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
